import * as React from 'react';
import { MessageSquarePlus, Pencil, Trash2, ChevronLeft, ChevronRight, History, PanelLeftOpen, ArrowRightLeft } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import {
  type AgentAccessScope, type AgentConversation,
  getListAgentConversationsQueryKey, getGetAgentConversationQueryKey,
  useListAgentConversations, useCreateAgentConversation, useUpdateAgentConversation
} from '@cdf/api-client-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { AgentChatRow } from './agent-chat-row';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { agentWorkspaceParams, workspaceError } from './workspace-utils';
import { AgentThreadHistory } from './agent-thread-history';
import { AgentChatHandoff } from './agent-chat-handoff';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '@/components/ui/sheet';

interface SavedChatsProps {
  scope: AgentAccessScope;
  mobile?: boolean;
  rail?: boolean;
  pickerOpen?: boolean;
  onPickerOpenChange?: (open: boolean) => void;
  triggerRef?: React.RefObject<HTMLButtonElement | null>;
  researchMode?: 'internal' | 'public_research';
  identity: string;
  selected?: AgentConversation;
  selectedId?: string;
  disabled: boolean;
  beforeNavigate?: () => boolean;
  onBusyChange?: (busy: boolean) => void;
  onSelect: (conversation: AgentConversation) => void;
  onDeleted: () => void;
}

export function AgentSavedChats({ scope, mobile = false, rail = false, pickerOpen = false, onPickerOpenChange, triggerRef, researchMode = 'internal', identity, selected, selectedId, disabled, beforeNavigate, onBusyChange, onSelect, onDeleted }: SavedChatsProps) {
  const client = useQueryClient();
  const [offset, setOffset] = React.useState(0);
  const [rename, setRename] = React.useState(false);
  const [title, setTitle] = React.useState('');
  const [confirmDelete, setConfirmDelete] = React.useState(false);
  const [handoff, setHandoff] = React.useState<AgentConversation>();
  const [actionTarget, setActionTarget] = React.useState<AgentConversation>();
  const titleInput = React.useRef<HTMLInputElement>(null);
  const deleteConfirmation = React.useRef<HTMLButtonElement>(null);
  const [error, setError] = React.useState<string>();
  const params = { mode: researchMode, ...agentWorkspaceParams(scope), limit: 20, offset };
  const query = useListAgentConversations(params, { query: {
    queryKey: [...getListAgentConversationsQueryKey(params), identity], retry: false
  } });
  const controller = React.useRef(new AbortController());
  const alive = React.useRef(true);
  const inFlight = React.useRef(false);
  React.useEffect(() => {
    if (query.data && offset > 0 && offset >= query.data.total) setOffset(Math.max(0, Math.floor((query.data.total - 1) / 20) * 20));
  }, [query.data, offset]);
  React.useEffect(() => {
    alive.current = true;
    if (controller.current.signal.aborted) controller.current = new AbortController();
    return () => { alive.current = false; controller.current.abort(); onBusyChange?.(false); };
  }, []);
  const request = { get signal() { return controller.current.signal; } };
  const create = useCreateAgentConversation({ mutation: { retry: false }, request });
  const update = useUpdateAgentConversation({ mutation: { retry: false }, request });
  const busy = disabled || create.isPending || update.isPending;
  React.useEffect(() => { setRename(false); setConfirmDelete(false); setActionTarget(undefined); setError(undefined); }, [selectedId]);
  const managedChat = actionTarget ?? selected;
  function canLeaveManagement() {
    return !rename || title === managedChat?.title || window.confirm('Discard the unsaved chat title?');
  }
  function manageChat(row: AgentConversation, action: 'rename' | 'delete') {
    if (busy || inFlight.current) return;
    if (!canLeaveManagement()) return;
    setActionTarget(row);
    setTitle(row.title);
    setRename(action === 'rename');
    setConfirmDelete(action === 'delete');
    setError(undefined);
  }
  function cancelManagement() {
    setRename(false);
    setConfirmDelete(false);
    setActionTarget(undefined);
    setError(undefined);
  }
  async function refresh() {
    await client.invalidateQueries({ queryKey: getListAgentConversationsQueryKey() });
    if (selectedId) await client.invalidateQueries({ queryKey: getGetAgentConversationQueryKey(selectedId, agentWorkspaceParams(scope)) });
  }
  async function newChat() {
    if (busy || inFlight.current || !canLeaveManagement() || beforeNavigate?.() === false) return;
    inFlight.current = true;
    onBusyChange?.(true);
    setError(undefined);
    try {
      const row = await create.mutateAsync({ data: { accessScope: scope, mode: researchMode, title: researchMode === 'public_research' ? 'New public research' : 'New chat' } });
      if (!alive.current) return;
      await refresh();
      if (alive.current) onSelect(row);
    } catch (failure) { if (alive.current) setError(workspaceError(failure)); }
    finally { inFlight.current = false; if (alive.current) onBusyChange?.(false); }
  }
  async function change(deleted = false) {
    if (!managedChat || busy || inFlight.current || (!deleted && !title.trim()) || (deleted && managedChat.id === selectedId && beforeNavigate?.() === false)) return;
    const targetId = managedChat.id;
    inFlight.current = true;
    onBusyChange?.(true);
    setError(undefined);
    try {
      await update.mutateAsync({ id: targetId, data: {
        accessScope: scope, expectedRevision: managedChat.revision,
        ...(deleted ? { deleted: true } : { title: title.trim() })
      } });
      if (!alive.current) return;
      await refresh();
      if (!alive.current) return;
      cancelManagement();
      if (deleted && targetId === selectedId) onDeleted();
    } catch (failure) { if (alive.current) { setError(workspaceError(failure)); await refresh(); } }
    finally { inFlight.current = false; if (alive.current) onBusyChange?.(false); }
  }
  const body = <aside className="flex h-full min-h-0 min-w-0 flex-col gap-2 p-2" aria-label="Private saved chats">
    <div className="flex shrink-0 flex-wrap items-center justify-between gap-1"><h2 className="text-sm font-semibold">Your conversations</h2><Button size="sm" variant="outline" className="h-10 justify-start gap-2" onClick={() => void newChat()} disabled={busy}><MessageSquarePlus className="size-3.5" />New conversation</Button></div>
    <p className="text-xs text-muted-foreground">Private to you in this scope.</p>
    {query.isPending && <p role="status" className="text-sm text-muted-foreground">Loading your conversations…</p>}
    {query.isError && <div role="alert" className="text-sm"><p>{workspaceError(query.error)}</p><Button variant="outline" size="sm" onClick={() => void query.refetch()}>Retry history</Button></div>}
    {!query.isError && query.data?.items.length === 0 && <p className="text-sm text-muted-foreground">No conversations in this scope yet.</p>}
    <div className="min-h-0 flex-1 space-y-1 overflow-y-auto">
      {query.data?.items.map(row => <AgentChatRow key={row.id} title={row.title} selected={row.id === selectedId} disabled={busy}
        onSelect={() => { if (row.id !== selectedId && !inFlight.current && canLeaveManagement() && beforeNavigate?.() !== false) onSelect(row); }}
        onRename={() => manageChat(row, 'rename')} onDelete={() => manageChat(row, 'delete')}
        onCloseAutoFocus={event => {
          if (rename || confirmDelete) {
            event.preventDefault();
            if (rename) titleInput.current?.focus(); else deleteConfirmation.current?.focus();
          }
        }} />)}
    </div>
    <p className="text-xs text-muted-foreground" aria-live="polite">{query.data?.total ? `${offset + 1}–${Math.min(offset + 20, query.data.total)} of ${query.data.total} chats` : '0 chats'}</p>
    <div className="flex flex-wrap items-center justify-between gap-2">
      <Button size="sm" variant="outline" disabled={offset === 0 || query.isFetching || busy} onClick={() => setOffset(Math.max(0, offset - 20))} aria-label="Previous chats"><ChevronLeft className="size-3" /><span className="sr-only">Previous chats</span></Button>
      <Button size="sm" variant="outline" disabled={!query.data || offset + 20 >= query.data.total || query.isFetching || busy} onClick={() => setOffset(offset + 20)} aria-label="Next chats"><ChevronRight className="size-3" /><span className="sr-only">Next chats</span></Button>
    </div>
    {managedChat && <div className="space-y-2 border-t pt-3">
      <p className="break-words text-xs font-medium">{managedChat.title}</p>
      <div className="flex flex-wrap gap-2"><Button size="sm" variant="ghost" aria-label="Rename chat" disabled={busy} onClick={() => manageChat(managedChat, 'rename')}><Pencil className="size-3" />Rename</Button><Button size="sm" variant="ghost" aria-label="Delete chat" disabled={busy} onClick={() => manageChat(managedChat, 'delete')}><Trash2 className="size-3" />Delete</Button><Button size="sm" variant="ghost" aria-label="Hand off chat" disabled={busy || !canLeaveManagement()} onClick={() => setHandoff(managedChat)}><ArrowRightLeft className="size-3" />Hand off</Button></div>
      {rename && <form className="space-y-2" onSubmit={event => { event.preventDefault(); void change(); }}>
        <Input ref={titleInput} aria-label="Chat title" value={title} maxLength={200} onChange={event => setTitle(event.target.value)} autoFocus />
        <div className="flex flex-wrap gap-2"><Button size="sm" disabled={!title.trim() || busy}>Save title</Button><Button type="button" size="sm" variant="ghost" disabled={busy} onClick={cancelManagement}>Cancel rename</Button></div>
      </form>}
      {confirmDelete && <div className="space-y-2"><p className="text-xs">Remove this chat from your saved history?</p><Button ref={deleteConfirmation} size="sm" variant="outline" disabled={busy} onClick={() => void change(true)}>Confirm delete chat</Button><Button size="sm" variant="ghost" disabled={busy} onClick={cancelManagement}>Keep chat</Button></div>}
    </div>}
    {error && <p role="alert" className="text-sm text-destructive">{error}</p>}
    {handoff && <AgentChatHandoff scope={scope} mode={researchMode} chat={handoff} open onOpenChange={next => { if (!next) setHandoff(undefined); }} />}
  </aside>;
  if (!mobile) return rail
    ? <AgentThreadHistory desktop open={pickerOpen} onOpenChange={open => onPickerOpenChange?.(open)} triggerRef={triggerRef}>{body}</AgentThreadHistory>
    : body;
  if (rail) return <aside aria-label="Chats rail" className="flex w-8 shrink-0 flex-col items-center border-r bg-muted/30">
    <Button type="button" size="icon" variant="ghost" className="h-10 w-8 shrink-0" ref={triggerRef} aria-label="Saved chats" title="Expand chats" aria-expanded={pickerOpen} disabled={busy} onClick={() => onPickerOpenChange?.(true)}><PanelLeftOpen className="size-4" /></Button>
    <span aria-hidden className="mt-2 rotate-180 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground [writing-mode:vertical-rl]">Chats</span>
    <Sheet open={pickerOpen} onOpenChange={open => { if (!busy && !inFlight.current) onPickerOpenChange?.(open); }}>
      <SheetContent side="left" className="flex w-[calc(100%-2rem)] max-w-sm flex-col gap-2 overflow-hidden p-3" onCloseAutoFocus={event => { if (triggerRef?.current) { event.preventDefault(); triggerRef.current.focus(); } }}>
        <SheetHeader className="shrink-0 pr-8 text-left"><SheetTitle>Your conversations</SheetTitle><SheetDescription>Private to you in this scope. Other people can’t see them.</SheetDescription></SheetHeader>
        <div className="min-h-0 flex-1 overflow-y-auto">{body}</div>
      </SheetContent>
    </Sheet>
  </aside>;
  return <div className="shrink-0 border-b bg-muted/20 p-2">
    <div className="flex min-w-0 items-center gap-1">
      <select aria-label="Agent conversation" className="h-8 min-w-0 flex-1 rounded-md border bg-background px-2 text-xs" disabled={busy || query.isPending} value={query.data?.items.some(row => row.id === selectedId) ? selectedId : ''} onChange={event => {
        const row = query.data?.items.find(item => item.id === event.target.value);
        if (row && row.id !== selectedId && !inFlight.current && canLeaveManagement() && beforeNavigate?.() !== false) onSelect(row);
      }}>
        <option value="">{selected?.title ?? 'Choose a conversation'}</option>
        {query.data?.items.map(row => <option key={row.id} value={row.id}>{row.title}</option>)}
      </select>
      <Button type="button" size="icon" variant="ghost" className="size-8 shrink-0" ref={triggerRef} aria-label="Saved chats" aria-expanded={pickerOpen} disabled={busy} onClick={() => onPickerOpenChange?.(true)}><History className="size-3.5" /></Button>
      <Button type="button" size="icon" variant="outline" className="size-8 shrink-0" aria-label="New conversation" disabled={busy} onClick={() => void newChat()}><MessageSquarePlus className="size-3.5" /></Button>
      <Button type="button" size="icon" variant="ghost" className="size-8 shrink-0" aria-label="Rename chat" disabled={busy || !selected} onClick={() => { if (!selected) return; manageChat(selected, 'rename'); onPickerOpenChange?.(true); }}><Pencil className="size-3.5" /></Button>
      <Button type="button" size="icon" variant="ghost" className="size-8 shrink-0" aria-label="Delete chat" disabled={busy || !selected} onClick={() => { if (!selected) return; manageChat(selected, 'delete'); onPickerOpenChange?.(true); }}><Trash2 className="size-3.5" /></Button>
    </div>
    <p className="mt-1 text-[10px] text-muted-foreground">Use Saved chats to browse earlier and later pages.</p>
    <Dialog open={pickerOpen} onOpenChange={onPickerOpenChange}><DialogContent className="flex max-h-[85dvh] flex-col overflow-y-auto" onCloseAutoFocus={event => { if (triggerRef?.current) { event.preventDefault(); triggerRef.current.focus(); } }}>
      <DialogHeader><DialogTitle>Your conversations</DialogTitle><DialogDescription>Private to you in this scope. Other people can’t see them.</DialogDescription></DialogHeader>{body}
    </DialogContent></Dialog>
    {!pickerOpen && error && <p role="alert" className="text-xs">{error}</p>}
  </div>;
}
